<template>
    <section class="vh-auto">
        <div v-if="mission != null" class="container h-100">
            <div class="row d-flex justify-content-center align-items-center h-100">
                <div class="col col-lg-12 mb-4 mb-lg-0">
                    <div class="card mb-3 shadow p-3 mb-5 bg-body rounded" style="border-radius: .5rem;">
                        <div class="card-body p-4">
                            <div class="row align-items-center mb-2">
                                <div class="col">
                                    <h4 class="mb-0 text-truncate d-flex align-items-center gap-2">
                                        {{ mission.name }} #{{ mission.id }}
                                        <ButtonDisplay
                                            v-if="mission?.status === 'DRAFT' || mission?.status === 'PENDING' || mission?.status === 'ONGOING'"
                                            :type="'edit'" :text="'Modifier la mission'" size="sm" class="mx-3"
                                            @click="goToSetup" />

                                        <ButtonDisplay v-if="canCancel" :type="'cancel'" :text="'Annuler la mission'"
                                            size="sm" class="mx-3" @click="onCancelClick" />
                                    </h4>
                                    <MissionStatusDisplay :status="mission.status" />
                                </div>
                                <div class="col-auto text-end">
                                    <MapLinkDisplay :link="mapLink" class="map-thumb" />
                                </div>
                            </div>

                            <!-- Corridoring display Section -->
                            <template v-if="mission?.corridoring">
                                <hr class="mt-0 mb-4" />
                                <div class="row pt-1 mb-2">
                                    <h6>Corridoring</h6>
                                    <div class="col-12 mb-6 text-center">
                                        <router-link :to="'/corridoringmap/' + corridoring.id">
                                            {{ corridoring.name }}
                                        </router-link>
                                    </div>
                                </div>
                                <div class="row pt-1">
                                    <div class="col-12 mb-6">
                                        <CorridoringDisplay :corridoring="corridoring" />
                                    </div>
                                </div>
                            </template>

                            <!-- Prepared infos -->
                            <hr class="my-5" />
                            <div class="row pt-1 mb-4 d-flex justify-content-center">
                                <div class="col-12 small">
                                    <MissionTransporteurCard :mission="mission" uiMode="view" />
                                </div>
                            </div>

                            <div class="row pt-1 mb-4 d-flex justify-content-center">
                                <div class="col-12 small">
                                    <ConvoyListCompact :mission="mission" />
                                </div>
                            </div>

                            <hr class="my-5" />
                            <div class="row pt-1 mb-4 d-flex justify-content-center">
                                <div class="col-6">
                                    <MembersListCompact :mission="mission" />
                                </div>
                                <div class="col-6">
                                    <MissionPlateSection :mission="mission" :uiMode="'view'"
                                        @addPlate="$emit('addPlate', $event)"
                                        @removePlate="$emit('removePlate', $event)" />
                                </div>
                            </div>

                            <!-- Start / end location display Section -->
                            <template v-if="mission?.startDate">
                                <hr class="mt-0 mb-3" />
                                <div class="row pt-1 mb-4">
                                    <div v-if="mission?.startDate" class="col-12 col-md-6 small mb-3 mb-md-0">
                                        <h6>Début</h6>
                                        <span class="text-muted">{{ mission.displayStartDate }}</span><br />
                                        <span>{{ mission.startAddress }}</span><br />
                                        <LatLongDisplay :latitude="mission.startLatitude"
                                            :longitude="mission.startLongitude" />
                                    </div>
                                    <div v-if="mission?.endDate" class="col-12 col-md-6 small">
                                        <h6>Fin</h6>
                                        <div>
                                            <span class="text-muted">{{ mission.displayEndDate }}</span><br />
                                            <span>{{ mission.endAddress }}</span><br />
                                            <LatLongDisplay :latitude="mission.endLatitude"
                                                :longitude="mission.endLongitude" />
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- Start / end location display Section -->
                            <template v-if="mission?.status === 'FINISHED' || mission?.status === 'CANCELED'">
                                <hr class="mt-3 mb-5" />
                                <div class="row pt-1 mb-4">
                                    <div class="col-6 small">
                                        <CommentsList v-if="mission" ref="commentsList" :missionId="mission.id" />
                                    </div>
                                    <div class="col-6 small">
                                        <CommentAdd :disabled="!(this.$isOperator || this.$isAdmin)"
                                            @submit="onSubmitComment" />
                                    </div>
                                </div>
                            </template>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import MissionStatusDisplay from '@/components/display/MissionStatusDisplay.vue'
import MapLinkDisplay from '@/components/display/MapLinkDisplay.vue'
import MissionPlateSection from '../../Components/MissionPlateSection.vue'
import ConvoyListCompact from '../../Components/Convoy/ConvoyListCompact.vue'
import CorridoringDisplay from '@/components/display/CorridoringDisplay.vue';
import LatLongDisplay from '@/components/display/LatLongDisplay.vue';
import MembersListCompact from '../../Components/Members/MembersListCompact.vue'
import MissionTransporteurCard from '../../Components/MissionTransporteurCard.vue'
import CommentAdd from '../../Components/CommentAdd.vue'
import CommentsList from '../../Components/CommentsList.vue'
import ButtonDisplay from '@/components/display/ButtonDisplay.vue'
import MissionService from '@/services/mission.service'


export default {
    props: ["mission"],
    components: {
        MissionStatusDisplay,
        MapLinkDisplay,
        MissionPlateSection,
        ConvoyListCompact,
        CorridoringDisplay,
        LatLongDisplay,
        MembersListCompact,
        ButtonDisplay,
        MissionTransporteurCard,
        CommentAdd,
        CommentsList
    },
    data() {
        return {
            isCanceling: false,
        };
    },
    computed: {
        mapLink() {
            if (!this.mission) return '';
            return this.mission.status === 'FINISHED'
                ? `/missionhistorymap/${this.mission.id}`
                : `/missionmap/${this.mission.id}`;
        },
        corridoring() {
            return this.mission?.corridoring;
        },
        canCancel() {
            if (!this.mission) return false;
            const allowed = ['DRAFT', 'PENDING', 'ONGOING'];
            return (this.$isOperator || this.$isAdmin) && allowed.includes(this.mission.status);
        },
    },
    methods: {
        onSubmitComment(content) {
            this.$emit("addComment", content);
        },
        reloadComments() {
            this.$refs.commentsList?.reload();
        },
        goToSetup() {
            if (!this.mission?.id) return;
            // On envoie vers le parent du wizard qui charge la mission et redirige sur la 1ère étape
            this.$router.push(`/mission/${this.mission.id}/setup/name`);
        },
        async onCancelClick() {
            if (this.isCanceling || !this.mission?.id) return;
            const base = "Confirmez-vous l'annulation de cette mission ?";
            const extra = this.mission?.status === 'ONGOING'
                ? "\n\n⚠️ Cette mission est EN COURS : elle sera marquée comme ANNULÉE."
                : "";
            if (!window.confirm(base + extra)) return;
            this.isCanceling = true;
            try {
                await MissionService.cancelMission(this.mission.id);
                // Laisse le parent décider quoi faire (recharger, naviguer, etc.)
                this.$emit('canceled', this.mission.id);
            } catch (e) {
                // Simple feedback minimaliste
                const msg = e?.response?.data?.message || 'Échec de l’annulation de la mission.';
                alert(msg);
            } finally {
                this.isCanceling = false;
            }
        },
    }
}
</script>

<style scoped>
/* Vignette compacte et propre */
.map-thumb {
    width: 120px;
    /* ajuste à 96/128 selon ton goût */
    height: 80px;
    border-radius: .5rem;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, .08);
    display: inline-block;
    /* évite l'étirement */
}

/* Force l'image interne à remplir sans déformer (selon le markup de MapLinkDisplay) */
.map-thumb :deep(img),
.map-thumb img,
.map-thumb>* {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

/* Optionnel : légèrement plus petite sur mobile */
@media (max-width: 576px) {
    .map-thumb {
        width: 96px;
        height: 64px;
    }
}

.locked-section {
    position: relative;
    filter: grayscale(0.8);
    opacity: 0.2;
    pointer-events: none;
}

.locked-section::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.35);
    border-radius: .5rem;
}
</style>